<template>
  <div class="fixed inset-0 flex items-center justify-center z-50">
    <div class="absolute inset-0 bg-black/20" @click="cancel"></div>
    <div class="relative bg-white rounded-2xl shadow-2xl p-5" style="width: 440px;">

      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <div
            class="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0"
            :class="{ 'animate-pulse': isRecording && !isPaused }"
          ></div>
          <span class="font-semibold text-gray-800 text-sm">
            {{ isPaused ? 'Paused' : (isRecording ? 'Recording' : 'Starting…') }}
          </span>
        </div>
        <div class="flex items-center gap-4">
          <button @click="cancel" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 4L4 14M4 4L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <button
            @click="save"
            :disabled="!hasStarted || isSaving"
            class="transition-colors"
            :class="hasStarted && !isSaving ? 'text-gray-700 hover:text-gray-900' : 'text-gray-300 cursor-not-allowed'"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 10L8 15L17 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Waveform -->
      <div class="flex items-center gap-[3px] overflow-hidden h-16 mb-3 px-1" style="min-height: 64px;">
        <div
          v-for="(bar, i) in waveformBars"
          :key="i"
          class="rounded-full shrink-0 transition-all duration-75"
          :style="barStyle(bar, i)"
        ></div>
      </div>

      <!-- Timer -->
      <div class="text-center mb-5">
        <span class="font-bold text-gray-700 tabular-nums" style="font-size: 15px; letter-spacing: 0.02em;">
          {{ formattedElapsed }}
        </span>
      </div>

      <!-- Controls -->
      <div class="flex items-center justify-center gap-5">
        <!-- Reset -->
        <button
          @click="resetRecording"
          :disabled="!hasStarted"
          class="w-11 h-11 rounded-full border border-gray-200 flex items-center justify-center transition-colors"
          :class="hasStarted ? 'hover:bg-gray-50 text-gray-600' : 'text-gray-300 cursor-not-allowed'"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 9C2.5 5.41 5.41 2.5 9 2.5C11.18 2.5 13.1 3.55 14.32 5.18L13 6.5H17V2.5L15.54 3.96C13.96 1.9 11.63 0.5 9 0.5C4.31 0.5 0.5 4.31 0.5 9H2.5Z" fill="currentColor"/>
            <path d="M15.5 9C15.5 12.59 12.59 15.5 9 15.5C6.82 15.5 4.9 14.45 3.68 12.82L5 11.5H1V15.5L2.46 14.04C4.04 16.1 6.37 17.5 9 17.5C13.69 17.5 17.5 13.69 17.5 9H15.5Z" fill="currentColor"/>
          </svg>
        </button>

        <!-- Pause / Resume -->
        <button
          @click="togglePause"
          :disabled="!isRecording && !isPaused"
          class="w-14 h-14 rounded-full flex items-center justify-center transition-colors"
          :class="isRecording || isPaused ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-100 text-gray-300 cursor-not-allowed'"
        >
          <!-- Pause icon -->
          <svg v-if="!isPaused" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="3" width="4" height="14" rx="1.5" fill="currentColor"/>
            <rect x="12" y="3" width="4" height="14" rx="1.5" fill="currentColor"/>
          </svg>
          <!-- Play icon (resume) -->
          <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 3.5L16 10L5 16.5V3.5Z" fill="currentColor"/>
          </svg>
        </button>

        <!-- Stop -->
        <button
          @click="save"
          :disabled="!isRecording && !isPaused"
          class="w-11 h-11 rounded-full border border-gray-200 flex items-center justify-center transition-colors"
          :class="isRecording || isPaused ? 'hover:bg-gray-50 text-gray-600' : 'text-gray-300 cursor-not-allowed border-gray-100'"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="12" height="12" rx="1.5" fill="currentColor"/>
          </svg>
        </button>
      </div>

      <!-- Error -->
      <div v-if="errorMsg" class="mt-3 text-red-500 text-xs text-center">{{ errorMsg }}</div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

const BAR_COUNT = 70;
const BAR_MIN_H = 3;
const BAR_MAX_H = 52;
const DOT_H = 4;

export default {
  name: "AudioRecorder",
  emits: ["saved", "cancel"],
  setup(_, { emit }) {
    const isRecording = ref(false);
    const isPaused = ref(false);
    const hasStarted = ref(false);
    const isSaving = ref(false);
    const errorMsg = ref("");

    // Timer — centiseconds (1/100 s)
    const elapsed = ref(0); // in centiseconds
    let timerInterval = null;

    // Waveform — array of 0-1 amplitude values
    const waveformBars = ref(Array(BAR_COUNT).fill(0));
    const barsPushed = ref(0); // counts bars pushed in, caps at BAR_COUNT

    let mediaRecorder = null;
    let chunks = [];
    let audioCtx = null;
    let analyser = null;
    let animFrame = null;
    let stream = null;

    // ── Timer format: MM.SS.cs ──
    const formattedElapsed = computed(() => {
      const total = elapsed.value;
      const cs = total % 100;
      const totalSec = Math.floor(total / 100);
      const s = totalSec % 60;
      const m = Math.floor(totalSec / 60);
      return `${String(m).padStart(2, "0")}.${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
    });

    // ── Bar style ──
    function barStyle(amplitude, index) {
      const recorded = filledCount();
      const isRecordedZone = index < recorded;
      if (isRecordedZone && amplitude > 0) {
        const h = Math.max(BAR_MIN_H, Math.round(amplitude * BAR_MAX_H));
        return {
          width: "3px",
          height: h + "px",
          backgroundColor: "#F87171",
          opacity: 0.85 + amplitude * 0.15,
        };
      }
      // dot zone
      return {
        width: "3px",
        height: DOT_H + "px",
        borderRadius: "9999px",
        backgroundColor: "#FECACA",
      };
    }

    function filledCount() {
      return barsPushed.value;
    }

    // ── Audio analysis loop ──
    function startAnalysis() {
      if (!analyser) return;
      const data = new Uint8Array(analyser.frequencyBinCount);

      function tick() {
        if (!isRecording.value || isPaused.value) {
          animFrame = requestAnimationFrame(tick);
          return;
        }
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const amplitude = Math.min(1, rms * 4);

        // Push new bar, drop oldest
        const bars = [...waveformBars.value];
        bars.shift();
        bars.push(amplitude);
        waveformBars.value = bars;
        if (barsPushed.value < BAR_COUNT) barsPushed.value++;

        animFrame = requestAnimationFrame(tick);
      }
      animFrame = requestAnimationFrame(tick);
    }

    // ── Start recording ──
    async function startRecording() {
      errorMsg.value = "";
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        audioCtx = new AudioContext();
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        const source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);

        chunks = [];
        const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "audio/webm";
        mediaRecorder = new MediaRecorder(stream, { mimeType });
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        mediaRecorder.onstart = () => {
          isRecording.value = true;
          hasStarted.value = true;
          elapsed.value = 0;
          timerInterval = setInterval(() => {
            if (!isPaused.value) elapsed.value++;
          }, 10); // every 10ms = 1 centisecond
          startAnalysis();
        };
        mediaRecorder.onstop = () => {
          isRecording.value = false;
        };
        mediaRecorder.start(100); // collect in 100ms chunks
      } catch (err) {
        errorMsg.value = "Microphone access denied. Please allow access and try again.";
      }
    }

    // ── Stop ──
    function stopRecording() {
      clearInterval(timerInterval);
      if (animFrame) cancelAnimationFrame(animFrame);
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
      if (stream) stream.getTracks().forEach((t) => t.stop());
    }

    // ── Pause / Resume ──
    function togglePause() {
      if (!mediaRecorder) return;
      if (isPaused.value) {
        mediaRecorder.resume();
        isPaused.value = false;
      } else {
        mediaRecorder.pause();
        isPaused.value = true;
      }
    }

    // ── Reset ──
    function resetRecording() {
      stopRecording();
      elapsed.value = 0;
      isPaused.value = false;
      waveformBars.value = Array(BAR_COUNT).fill(0);
      barsPushed.value = 0;
      chunks = [];
      startRecording();
    }

    // ── Save (✓ button) ──
    function save() {
      if (!hasStarted.value || isSaving.value) return;
      isSaving.value = true;
      stopRecording();

      // Wait a tick for onstop to fire and flush final chunk
      setTimeout(() => {
        if (chunks.length === 0) {
          isSaving.value = false;
          errorMsg.value = "No audio captured.";
          return;
        }
        const mimeType = mediaRecorder?.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type: mimeType });
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(",")[1];
          emit("saved", { base64, mimeType });
        };
        reader.readAsDataURL(blob);
      }, 300);
    }

    // ── Cancel ──
    function cancel() {
      if (isSaving.value) return;
      stopRecording();
      emit("cancel");
    }

    onMounted(() => {
      startRecording();
    });

    onBeforeUnmount(() => {
      clearInterval(timerInterval);
      if (animFrame) cancelAnimationFrame(animFrame);
      if (mediaRecorder && mediaRecorder.state !== "inactive") mediaRecorder.stop();
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (audioCtx) audioCtx.close();
    });

    return {
      isRecording, isPaused, hasStarted, isSaving, errorMsg,
      waveformBars, formattedElapsed,
      barStyle, togglePause, stopRecording, resetRecording, save, cancel,
    };
  },
};
</script>
